import { useMemo, type JSX } from 'react'

interface PeriodSelectorProps {
  value: string // 'YYYY-MM'
  cycleLabel?: string
  onChange: (period: string) => void
}

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

export default function PeriodSelector({
  value,
  cycleLabel,
  onChange,
}: PeriodSelectorProps): JSX.Element {
  // Generate past 12 months including current month
  const options = useMemo(() => {
    const list: Array<{ value: string; label: string; isPast: boolean }> = []
    const now = new Date()
    const currentYear = now.getUTCFullYear()
    const currentMonth = now.getUTCMonth() + 1 // 1-12

    for (let i = 0; i < 12; i++) {
      const d = new Date(Date.UTC(currentYear, currentMonth - 1 - i, 1))
      const y = d.getUTCFullYear()
      const m = d.getUTCMonth() + 1
      const val = `${y}-${String(m).padStart(2, '0')}`
      const label = `${MONTH_NAMES[m - 1]} ${y}`
      const isPast = y < currentYear || (y === currentYear && m < currentMonth)
      list.push({ value: val, label, isPast })
    }
    return list
  }, [])

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className="font-medium text-gray-500">Period:</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Select billing period"
        className="rounded-sm border border-gray-300 bg-white px-2.5 py-1 text-xs font-semibold text-gray-700 shadow-xs focus:border-[#3454cf] focus:outline-hidden focus:ring-1 focus:ring-[#3454cf]"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label} {opt.isPast ? '(read-only)' : ''}
          </option>
        ))}
      </select>
      {cycleLabel && (
        <span className="text-[11px] text-gray-400">{cycleLabel}</span>
      )}
    </div>
  )
}
